import { __assign, __spread, __read, __rest } from 'tslib';
import { useMemo, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
/*
 * Copyright 2019 Seven Stripes Kabushiki Kaisha, MIT License
 *
 * Parts taken from react-codemirror2
 * Copyright 2019 Sal Niro, MIT License
 * https://github.com/scniro/react-codemirror2/blob/a633e7dd673ddf5bdb07e2ed664a03aa47159bfa/src/index.tsx
 */
// React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser. We need useLayoutEffect because we want
// `connect` to perform sync updates to a ref to save the latest props after
// a render is actually committed to the DOM.
// https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85#gistcomment-2911761

var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function useCodeMirror(unmemoizedOptions) {
  var config = useEditorConfiguration(unmemoizedOptions.config, unmemoizedOptions.docName);
  var options = useMemo(function () {
    return __assign(__assign({}, unmemoizedOptions), {
      config: config
    });
  }, [config, unmemoizedOptions]);
  var initialOptionsRef = useRef(options);
  var instanceRef = useRef(null);
  var externalRef = useRef();
  var nodePromiseRef = useRef();

  if (!externalRef.current) {
    nodePromiseRef.current = new Promise(function (resolve) {
      externalRef.current = function (element) {
        if (element) {
          resolve(element);
        }
      };
    });
  }

  useIsomorphicLayoutEffect(function () {
    if (instanceRef.current) {
      instanceRef.current.update(options);
    } else {
      initialOptionsRef.current = options;
    }
  });
  useEffect(function () {
    var importCodeMirror = options.importCodeMirror || defaultImportCodeMirror;
    var importCodeMirrorAddons = options.importCodeMirrorAddons || defaultImportCodeMirrorAddons;
    var isMounted = true;
    Promise.all([nodePromiseRef.current, importCodeMirror(), importCodeMirrorAddons()]).then(function (_a) {
      var _b = __read(_a, 2),
          node = _b[0],
          CodeMirror = _b[1];

      if (isMounted) {
        instanceRef.current = new CodeMirrorInstance(CodeMirror, node, initialOptionsRef.current);
      }
    });
    return function () {
      isMounted = false;

      if (instanceRef.current) {
        instanceRef.current.dispose();
      }
    }; // eslint-disable-next-line
  }, []);
  var editor = instanceRef.current && instanceRef.current.editor || undefined;
  return {
    config: config,
    editor: editor,
    focus: useCallback(function () {
      return editor && editor.focus();
    }, [editor]),
    ref: externalRef.current
  };
}

var CodeMirrorInstance =
/** @class */
function () {
  function CodeMirrorInstance(CodeMirror, placeholderNode, options) {
    var _this = this;

    this.handleBlur = function () {
      if (_this.options.onBlur) {
        _this.options.onBlur();
      }
    };

    this.handleChanges = function (editor, changes) {
      // Ignore changes caused by this component
      if (changes.length === 1 && changes[0].origin === 'setValue') {
        return;
      }

      if (!_this.options.config.readOnly && _this.options.onChange) {
        _this.options.onChange(editor.getValue(), _this.options.docName, changes, editor.getDoc());
      }
    };

    this.handleCursor = function (editor) {
      if (_this.options.onCursor) {
        _this.options.onCursor(editor.getDoc().getCursor());
      }
    };

    this.handleFocus = function () {
      if (_this.options.onFocus) {
        _this.options.onFocus();
      }
    };

    this.handleGutterClick = function (editor, lineNumber, gutter, event) {
      if (_this.options.onGutterClick) {
        _this.options.onGutterClick(lineNumber, gutter, event);
      }
    };

    this.handleScroll = function (editor) {
      if (_this.options.onScroll) {
        _this.options.onScroll(editor.getScrollInfo());
      }
    };

    this.handleSelection = function (editor, data) {
      if (_this.options.onSelection) {
        _this.options.onSelection(data);
      }
    };

    this.handleViewportChange = function (editor, from, to) {
      if (_this.options.onViewportChange) {
        _this.options.onViewportChange(from, to);
      }
    };

    this.CodeMirror = CodeMirror;
    this.docs = {};
    this.options = options;
    var editor = this.editor = new CodeMirror(function (editorNode) {
      return placeholderNode.parentNode.replaceChild(editorNode, placeholderNode);
    }, options.config);
    this.updateDocIfRequired();
    editor.on('blur', this.handleBlur);
    editor.on('changes', this.handleChanges);
    editor.on('scroll', this.handleScroll);
    editor.on('cursor', this.handleCursor);
    editor.on('focus', this.handleFocus);
    editor.on('gutterClick', this.handleGutterClick);
    editor.on('beforeSelectionChange', this.handleSelection);
    editor.on('viewportChange', this.handleViewportChange);
  }

  CodeMirrorInstance.prototype.dispose = function () {
    // is there a lighter-weight way to remove the cm instance?
    if (this.editor) {
      // Need to swap out the old doc, otherwise it can't be used again on
      // future codemirror instances.
      this.editor.swapDoc(new this.CodeMirror.Doc(''));
      delete this.editor;
      delete this.CodeMirror;
    }
  };

  CodeMirrorInstance.prototype.update = function (options) {
    var _this = this;

    var oldOptions = options;
    this.options = options; // Switch out the doc if required

    var doc = this.updateDocIfRequired();
    var preserved = {
      cursor: null
    };

    if (!options.autoCursor && options.autoCursor !== undefined) {
      preserved.cursor = this.editor.getDoc().getCursor();
    } // If a new value has been set, and it differs from the editor's current
    // value, then set it in the editor.


    var didUpdateValue = false;

    if (options.value !== undefined && normalizeLineEndings(this.editor.getValue()) !== normalizeLineEndings(options.value) && (!oldOptions || oldOptions.value !== options.value)) {
      this.editor.setValue(options.value);
      didUpdateValue = true;
    } // Update any editor config options that have changed


    var configKeys = Object.keys(this.options.config);
    var configDelta = configKeys.filter(function (key) {
      return _this.editor.getOption(key) !== _this.options.config[key];
    });
    configDelta.forEach(function (key) {
      _this.editor.setOption(key, _this.options.config[key]);
    }); // Update selection if required

    if (didUpdateValue && options.selection && options.selection.ranges) {
      doc.setSelections(options.selection.ranges);

      if (options.selection.focus) {
        this.editor.focus();
      }
    } // Update cursor position if required


    if (options.cursor) {
      if (this.editor.getOption('autofocus')) {
        this.editor.focus();
      }

      doc.setCursor(preserved.cursor || options.cursor, undefined, options.autoScroll ? undefined : {
        scroll: false
      });
    } // Update scroll if required


    if (options.scroll) {
      this.editor.scrollTo(options.scroll.x, options.scroll.y);
    }
  }; // Returns true if the doc was updated


  CodeMirrorInstance.prototype.updateDocIfRequired = function () {
    var _a = this.options,
        mode = _a.config.mode,
        doc = _a.doc,
        docName = _a.docName,
        value = _a.value;

    if (!docName) {
      docName = '';
    }

    if (!doc) {
      doc = this.docs[docName];

      if (!doc) {
        doc = this.docs[docName] = new this.CodeMirror.Doc(value, mode);
      }
    }

    this.docs[docName] = doc;

    if (doc !== this.editor.getDoc()) {
      this.editor.swapDoc(doc);
    }

    return doc;
  };

  return CodeMirrorInstance;
}();

var modeAliases = {
  js: 'jsx',
  html: 'htmlmixed',
  md: 'markdown',
  mdx: 'markdown',
  scss: 'text/x-scss'
};

function defaultImportCodeMirror() {
  // CodeMirror crashes when loaded without a DOM, so let's avoid loading
  // it on the server.
  var codeMirrorPromise = typeof navigator === 'undefined' ? Promise.resolve([]) : Promise.all([import('codemirror')]);
  return codeMirrorPromise.then(function (_a) {
    var _b = __read(_a, 1),
        codeMirror = _b[0].default;

    return codeMirror;
  });
}

function defaultImportCodeMirrorAddons() {
  return Promise.all([import('codemirror/mode/jsx/jsx'), import('codemirror/mode/css/css'), import('codemirror/mode/markdown/markdown'), import('codemirror/mode/htmlmixed/htmlmixed'), import('codemirror/keymap/sublime'), import('codemirror/addon/comment/comment'), import('codemirror/addon/edit/closebrackets'), import('codemirror/addon/edit/matchbrackets'), import('codemirror/addon/edit/matchtags'), import('codemirror/addon/fold/xml-fold'), import('codemirror/addon/scroll/simplescrollbars'), import('codemirror/addon/selection/active-line'), import('codemirror/addon/search/search'), import('codemirror/addon/search/searchcursor'), import('codemirror/addon/dialog/dialog')]);
}

function getDefaultMode(docName) {
  return docName && docName.split('.').reverse()[0];
}

var defaultMatchTagsConfig = {
  bothTags: true
};
var defaultExtraKeysConfig = {
  Tab: function (cm) {
    var spaces = Array(cm.getOption('indentUnit') + 1).join(' ');
    cm.getDoc().replaceSelection(spaces);
  },
  'Cmd-/': function (cm) {
    cm.getDoc().listSelections().forEach(function () {
      cm.toggleComment({
        indent: true
      });
    });
  }
};

function useEditorConfiguration(_a, docName) {
  if (_a === void 0) {
    _a = {};
  }

  var extraKeys = _a.extraKeys,
      mode = _a.mode,
      rest = __rest(_a, ["extraKeys", "mode"]);

  var extraKeysConfig = useMemo(function () {
    return typeof extraKeys === 'string' ? extraKeys : __assign(__assign({}, defaultExtraKeysConfig), extraKeys);
  }, [extraKeys]);

  if (!mode) {
    mode = getDefaultMode(docName);
  }

  return useMemo(function () {
    return __assign({
      autoCloseBrackets: true,
      indentWithTabs: false,
      extraKeys: extraKeysConfig,
      keyMap: 'sublime',
      matchBrackets: true,
      matchTags: defaultMatchTagsConfig,
      mode: modeAliases[mode] || mode,
      scrollbarStyle: 'simple',
      smartIndent: false,
      styleActiveLine: true,
      tabSize: 2
    }, rest);
  }, __spread([extraKeys, mode], Object.values(rest)));
}

function normalizeLineEndings(str) {
  if (!str) return str;
  return str.replace(/\r\n|\r/g, '\n');
}

export { useCodeMirror };